import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfessionalLayout from '../components/ProfessionalLayout';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { INCIDENT_CATEGORIES } from '../lib/constants';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ProspectionTab from '../components/platform/ProspectionTab';
import {
  Building2,
  Users,
  Package,
  TrendingUp,
  CreditCard,
  Shield,
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Trash2,
  Play,
  Pause,
  RefreshCw,
  Crown,
  Clock,
  AlertCircle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  BarChart3,
} from 'lucide-react';

const INCIDENT_STATUS_COLORS = {
  open: 'bg-red-100 text-red-800',
  acknowledged: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-muted text-foreground',
};

const SUB_STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  trialing: 'bg-accent/10 text-accent',
  past_due: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-red-100 text-red-800',
  expired: 'bg-muted text-foreground',
};

const PLAN_NAMES = { starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise' };

const PlatformAdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const incidentCategoryLabels = (INCIDENT_CATEGORIES[language] || INCIDENT_CATEGORIES.fr);
  const [activeTab, setActiveTab] = useState('overview');

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Companies
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  // Users
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userCompanyFilter, setUserCompanyFilter] = useState('');
  const [usersPage, setUsersPage] = useState(1);

  // Subscriptions
  const [subscriptions, setSubscriptions] = useState([]);
  const [subStats, setSubStats] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);
  const [overrideCompany, setOverrideCompany] = useState(null);
  const [overridePlan, setOverridePlan] = useState('');
  const [overrideStatus, setOverrideStatus] = useState('');

  // Incidents
  const [incidents, setIncidents] = useState([]);
  const [incidentsPagination, setIncidentsPagination] = useState(null);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [incidentStatusFilter, setIncidentStatusFilter] = useState('');
  const [incidentCategoryFilter, setIncidentCategoryFilter] = useState('');
  const [incidentsPage, setIncidentsPage] = useState(1);
  const [resolveIncident, setResolveIncident] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');

  // Global
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Confirm dialog
  const [confirmAction, setConfirmAction] = useState(null);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get('/api/platform/analytics');
      setAnalytics(res.data);
    } catch (err) {
      setError(t('errorLoadingAnalytics'));
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  // Fetch companies
  const fetchCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    try {
      const res = await api.get('/api/platform/companies/activity');
      setCompanies(res.data.companies || []);
    } catch (err) {
      setError(t('errorLoadingCompanies'));
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = { page: usersPage, limit: 20 };
      if (userRoleFilter) params.role = userRoleFilter;
      if (userCompanyFilter) params.company_id = userCompanyFilter;
      if (userSearch) params.search = userSearch;
      const res = await api.get('/api/platform/users', { params });
      setUsers(res.data.data);
      setUsersPagination(res.data.pagination);
    } catch (err) {
      setError(t('errorLoadingUsers'));
    } finally {
      setUsersLoading(false);
    }
  }, [usersPage, userRoleFilter, userCompanyFilter, userSearch]);

  // Fetch subscriptions
  const fetchSubscriptions = useCallback(async () => {
    setSubsLoading(true);
    try {
      const res = await api.get('/api/subscriptions/admin/all');
      setSubscriptions(res.data.subscriptions || []);
      setSubStats(res.data.stats || null);
    } catch (err) {
      setError(t('errorLoadingSubscriptions'));
    } finally {
      setSubsLoading(false);
    }
  }, []);

  // Fetch incidents
  const fetchIncidents = useCallback(async () => {
    setIncidentsLoading(true);
    try {
      const params = { page: incidentsPage, limit: 20 };
      if (incidentStatusFilter) params.status_filter = incidentStatusFilter;
      if (incidentCategoryFilter) params.category = incidentCategoryFilter;
      const res = await api.get('/api/incidents', { params });
      setIncidents(res.data.data);
      setIncidentsPagination(res.data.pagination);
    } catch (err) {
      setError(t('errorLoadingIncidents'));
    } finally {
      setIncidentsLoading(false);
    }
  }, [incidentsPage, incidentStatusFilter, incidentCategoryFilter]);

  useEffect(() => {
    if (user?.role !== 'platform_admin') {
      navigate('/dashboard');
      return;
    }
    fetchAnalytics();
  }, [fetchAnalytics, user, navigate]);

  useEffect(() => {
    if (activeTab === 'companies') fetchCompanies();
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'subscriptions') fetchSubscriptions();
    if (activeTab === 'incidents') fetchIncidents();
  }, [activeTab, fetchCompanies, fetchUsers, fetchSubscriptions, fetchIncidents]);

  // Company actions
  const handleApproveCompany = async (companyId) => {
    try {
      await api.put(`/api/platform/companies/${companyId}/approve`);
      setSuccess(t('companyApproved'));
      fetchCompanies();
      fetchAnalytics();
    } catch (err) {
      setError(err.response?.data?.detail || t('errorApproval'));
    }
    setConfirmAction(null);
  };

  const handleSuspendCompany = async (companyId) => {
    try {
      await api.put(`/api/platform/companies/${companyId}/suspend`);
      setSuccess(t('companySuspended'));
      fetchCompanies();
      fetchAnalytics();
    } catch (err) {
      setError(err.response?.data?.detail || t('errorSuspension'));
    }
    setConfirmAction(null);
  };

  const handleActivateCompany = async (companyId) => {
    try {
      await api.put(`/api/platform/companies/${companyId}/activate`);
      setSuccess(t('companyReactivated'));
      fetchCompanies();
      fetchAnalytics();
    } catch (err) {
      setError(err.response?.data?.detail || t('errorActivation'));
    }
    setConfirmAction(null);
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await api.delete(`/api/platform/companies/${companyId}`);
      setSuccess(t('companyDeleted'));
      fetchCompanies();
      fetchAnalytics();
    } catch (err) {
      setError(err.response?.data?.detail || t('errorDeletion'));
    }
    setConfirmAction(null);
  };

  // Override subscription
  const handleOverride = async () => {
    if (!overrideCompany) return;
    try {
      const body = {};
      if (overridePlan) body.plan = overridePlan;
      if (overrideStatus) body.status = overrideStatus;
      await api.put(`/api/subscriptions/admin/${overrideCompany}`, body);
      setOverrideCompany(null);
      setSuccess(t('subscriptionUpdated'));
      fetchSubscriptions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Override failed');
    }
  };

  // Resolve incident
  const handleResolveIncident = async () => {
    if (!resolveIncident || !resolutionNote.trim()) return;
    try {
      await api.put(`/api/incidents/${resolveIncident}/resolve`, {
        resolution_note: resolutionNote,
        new_status: 'resolved',
      });
      setResolveIncident(null);
      setResolutionNote('');
      setSuccess(t('incidentResolved'));
      fetchIncidents();
    } catch (err) {
      setError(err.response?.data?.detail || t('errorResolution'));
    }
  };

  // Clear alerts after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const getCompanyStatusBadge = (status) => {
    const map = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-blue-100 text-blue-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      deleted: 'bg-red-100 text-red-800',
      inactive: 'bg-muted text-foreground',
    };
    return map[status] || 'bg-muted text-foreground';
  };

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Shield className="h-7 w-7 text-accent" />
          <div>
            <h1 className="text-2xl font-bold">{t('platformAdmin')}</h1>
            <p className="text-sm text-muted-foreground">{t('platformAdminDesc')}</p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="bg-green-500/10 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto gap-1">
            <TabsTrigger value="overview">{t('overview')}</TabsTrigger>
            <TabsTrigger value="companies">{t('companies')}</TabsTrigger>
            <TabsTrigger value="users">{t('users')}</TabsTrigger>
            <TabsTrigger value="subscriptions">{t('subscriptions')}</TabsTrigger>
            <TabsTrigger value="prospection">{language === 'en' ? 'Prospecting' : 'Prospection'}</TabsTrigger>
          </TabsList>

          {/* ============ OVERVIEW ============ */}
          <TabsContent value="overview" className="space-y-6">
            {analyticsLoading ? (
              <div className="flex justify-center py-20">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : analytics ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t('companies')}</CardTitle>
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{analytics.active_companies}</div>
                      <p className="text-xs text-muted-foreground">{analytics.total_companies} {t('total')}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t('activeUsers')}</CardTitle>
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{analytics.active_users}</div>
                      <p className="text-xs text-muted-foreground">{analytics.total_users} {t('total')}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t('packagesThisMonth')}</CardTitle>
                      <Package className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{analytics.packages_this_month}</div>
                      <p className="text-xs text-muted-foreground">{analytics.total_packages} {t('total')}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">MRR</CardTitle>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{analytics.mrr}&euro;</div>
                      <p className="text-xs text-muted-foreground">{analytics.open_incidents} {t('openIncidents')}</p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t('totalPackages')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{analytics.total_packages}</div>
                    <p className="text-sm text-muted-foreground mt-1">{t('allRegisteredPackages')}</p>
                  </CardContent>
                </Card>
              </>
            ) : null}
          </TabsContent>

          {/* ============ COMPANIES ============ */}
          <TabsContent value="companies" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">{t('allCompanies')}</h2>
              <Button variant="outline" size="sm" onClick={fetchCompanies}>
                <RefreshCw className="h-4 w-4 mr-1" /> {t('refresh')}
              </Button>
            </div>

            {companiesLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-3 pr-4">{t('name')}</th>
                      <th className="pb-3 pr-4">{t('code')}</th>
                      <th className="pb-3 pr-4">{t('country')}</th>
                      <th className="pb-3 pr-4">{t('status')}</th>
                      <th className="pb-3 pr-4 text-right">Utilisateurs</th>
                      <th className="pb-3 pr-4 text-right">Colis</th>
                      <th className="pb-3 pr-4 text-right">Voyages</th>
                      <th className="pb-3 pr-4 text-right">Incidents</th>
                      <th className="pb-3 pr-4">{t('contact')}</th>
                      <th className="pb-3">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.filter(c => c.status !== 'deleted').map((company) => (
                      <tr key={company.id} className="border-b last:border-0">
                        <td className="py-3 pr-4 font-medium">{company.name}</td>
                        <td className="py-3 pr-4 font-mono text-xs">{company.code}</td>
                        <td className="py-3 pr-4">{company.country}</td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCompanyStatusBadge(company.status)}`}>
                            {company.status}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-right tabular-nums">{company.users_count ?? '—'}</td>
                        <td className="py-3 pr-4 text-right tabular-nums font-medium">{company.packages_count ?? '—'}</td>
                        <td className="py-3 pr-4 text-right tabular-nums">{company.shipments_count ?? '—'}</td>
                        <td className="py-3 pr-4 text-right tabular-nums">
                          {company.open_incidents_count > 0
                            ? <span className="text-red-600 font-medium">{company.open_incidents_count}</span>
                            : (company.open_incidents_count ?? '—')}
                        </td>
                        <td className="py-3 pr-4 text-xs">{company.contact_email}</td>
                        <td className="py-3">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() => navigate(`/platform-admin/companies/${company.id}/activity`)}
                            >
                              <BarChart3 className="h-3 w-3 mr-1" /> Voir activité
                            </Button>
                            {company.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:text-green-800 h-7 px-2 text-xs"
                                onClick={() => setConfirmAction({ type: 'approve', id: company.id, name: company.name })}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" /> {t('approve')}
                              </Button>
                            )}
                            {company.status === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-yellow-600 hover:text-yellow-800 h-7 px-2 text-xs"
                                onClick={() => setConfirmAction({ type: 'suspend', id: company.id, name: company.name })}
                              >
                                <Pause className="h-3 w-3 mr-1" /> {t('suspend')}
                              </Button>
                            )}
                            {company.status === 'suspended' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:text-green-800 h-7 px-2 text-xs"
                                onClick={() => setConfirmAction({ type: 'activate', id: company.id, name: company.name })}
                              >
                                <Play className="h-3 w-3 mr-1" /> {t('activate')}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-800 h-7 px-2 text-xs"
                              onClick={() => setConfirmAction({ type: 'delete', id: company.id, name: company.name })}
                            >
                              <Trash2 className="h-3 w-3 mr-1" /> {t('delete')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {companies.filter(c => c.status !== 'deleted').length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-muted-foreground">{t('noCompanies')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* ============ USERS ============ */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-0 w-full sm:w-auto sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={t('searchNameOrEmail')}
                  value={userSearch}
                  onChange={(e) => { setUserSearch(e.target.value); setUsersPage(1); }}
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <select
                value={userRoleFilter}
                onChange={(e) => { setUserRoleFilter(e.target.value); setUsersPage(1); }}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">{t('allRoles')}</option>
                <option value="platform_admin">Platform Admin</option>
                <option value="super_admin">Super Admin</option>
                <option value="company_admin">Company Admin</option>
                <option value="country_admin">Country Admin</option>
                <option value="partner">Partner</option>
                <option value="local_partner">Local Partner</option>
                <option value="client">Client</option>
              </select>
              <select
                value={userCompanyFilter}
                onChange={(e) => { setUserCompanyFilter(e.target.value); setUsersPage(1); }}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">{t('allCompanies')}</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {usersLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-3 pr-4">{t('name')}</th>
                        <th className="pb-3 pr-4">{t('email')}</th>
                        <th className="pb-3 pr-4">{t('role')}</th>
                        <th className="pb-3 pr-4">{t('active')}</th>
                        <th className="pb-3">{t('country')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{u.name || '-'}</td>
                          <td className="py-3 pr-4">{u.email}</td>
                          <td className="py-3 pr-4">
                            <span className="px-2 py-0.5 rounded bg-muted text-xs font-medium">
                              {u.role?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            {u.is_active ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                          </td>
                          <td className="py-3">{u.country || '-'}</td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-muted-foreground">{t('noUsers')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {usersPagination && usersPagination.total_pages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-sm text-muted-foreground">
                      Page {usersPagination.page} / {usersPagination.total_pages} ({usersPagination.total} {t('results')})
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!usersPagination.has_prev}
                        onClick={() => setUsersPage((p) => p - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!usersPagination.has_next}
                        onClick={() => setUsersPage((p) => p + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ============ SUBSCRIPTIONS ============ */}
          <TabsContent value="prospection" className="space-y-4">
            <ProspectionTab />
          </TabsContent>

          <TabsContent value="subscriptions" className="space-y-4">
            {subsLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Stats cards */}
                {subStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-accent">{subStats.total}</div>
                        <div className="text-xs text-muted-foreground">{t('totalSubscriptions')}</div>
                      </CardContent>
                    </Card>
                    {Object.entries(subStats.plan_counts || {}).map(([plan, count]) => (
                      <Card key={plan}>
                        <CardContent className="pt-4 text-center">
                          <div className="text-2xl font-bold text-accent">{count}</div>
                          <div className="text-xs text-muted-foreground">{PLAN_NAMES[plan] || plan}</div>
                        </CardContent>
                      </Card>
                    ))}
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{subStats.mrr?.toFixed(0)}&euro;</div>
                        <div className="text-xs text-muted-foreground">MRR</div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Subscriptions table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-3 pr-4">{t('company')}</th>
                        <th className="pb-3 pr-4">{t('plan')}</th>
                        <th className="pb-3 pr-4">{t('status')}</th>
                        <th className="pb-3 pr-4">{t('period')}</th>
                        <th className="pb-3">{t('actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.id || sub.company_id} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{sub.company_name}</td>
                          <td className="py-3 pr-4">
                            <span className="px-2 py-0.5 rounded bg-muted text-xs font-medium">
                              {PLAN_NAMES[sub.plan] || sub.plan}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${SUB_STATUS_COLORS[sub.status] || ''}`}>
                              {sub.status}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-muted-foreground">
                            {sub.current_period_end
                              ? new Date(sub.current_period_end).toLocaleDateString('fr-FR')
                              : '-'}
                          </td>
                          <td className="py-3">
                            <button
                              onClick={() => {
                                setOverrideCompany(sub.company_id);
                                setOverridePlan(sub.plan);
                                setOverrideStatus(sub.status);
                              }}
                              className="text-accent hover:text-accent/80 text-xs font-medium"
                            >
                              {t('edit')}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {subscriptions.length === 0 && (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-muted-foreground">{t('noSubscriptions')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          {/* ============ INCIDENTS ============ */}
          <TabsContent value="incidents" className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <select
                value={incidentStatusFilter}
                onChange={(e) => { setIncidentStatusFilter(e.target.value); setIncidentsPage(1); }}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">{t('allStatuses')}</option>
                <option value="open">{t('open')}</option>
                <option value="acknowledged">{t('acknowledged')}</option>
                <option value="resolved">{t('resolved')}</option>
                <option value="closed">{t('closed')}</option>
              </select>
              <select
                value={incidentCategoryFilter}
                onChange={(e) => { setIncidentCategoryFilter(e.target.value); setIncidentsPage(1); }}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">{t('allCategories')}</option>
                {Object.entries(incidentCategoryLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <Button variant="outline" size="sm" onClick={fetchIncidents}>
                <RefreshCw className="h-4 w-4 mr-1" /> {t('refresh')}
              </Button>
            </div>

            {incidentsLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-3 pr-4">{t('package')}</th>
                        <th className="pb-3 pr-4">{t('category')}</th>
                        <th className="pb-3 pr-4">{t('reportedBy')}</th>
                        <th className="pb-3 pr-4">{t('status')}</th>
                        <th className="pb-3 pr-4">{t('date')}</th>
                        <th className="pb-3">{t('actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incidents.map((inc) => (
                        <tr key={inc.id} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-mono text-xs">{inc.package_oce_code || inc.package_id?.slice(0, 8)}</td>
                          <td className="py-3 pr-4">{incidentCategoryLabels[inc.category] || inc.category}</td>
                          <td className="py-3 pr-4">{inc.reporter_name || '-'}</td>
                          <td className="py-3 pr-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${INCIDENT_STATUS_COLORS[inc.status] || ''}`}>
                              {inc.status}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-xs text-muted-foreground">
                            {inc.created_at ? new Date(inc.created_at).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="py-3">
                            {(inc.status === 'open' || inc.status === 'acknowledged') && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs text-green-600"
                                onClick={() => { setResolveIncident(inc.id); setResolutionNote(''); }}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" /> {t('resolve')}
                              </Button>
                            )}
                            {inc.status === 'resolved' && (
                              <span className="text-xs text-muted-foreground">{t('resolved')}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {incidents.length === 0 && (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-muted-foreground">{t('noIncidents')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {incidentsPagination && incidentsPagination.total_pages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-sm text-muted-foreground">
                      Page {incidentsPagination.page} / {incidentsPagination.total_pages} ({incidentsPagination.total} {t('results')})
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!incidentsPagination.has_prev}
                        onClick={() => setIncidentsPage((p) => p - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!incidentsPagination.has_next}
                        onClick={() => setIncidentsPage((p) => p + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* ============ CONFIRM DIALOG ============ */}
        {confirmAction && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-bold mb-2">
                {confirmAction.type === 'approve' && t('confirmApproveCompany')}
                {confirmAction.type === 'suspend' && t('confirmSuspendCompany')}
                {confirmAction.type === 'activate' && t('confirmActivateCompany')}
                {confirmAction.type === 'delete' && t('confirmDeleteCompany')}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {confirmAction.type === 'approve' && t('confirmApproveDesc').replace('{name}', confirmAction.name)}
                {confirmAction.type === 'suspend' && t('confirmSuspendDesc').replace('{name}', confirmAction.name)}
                {confirmAction.type === 'activate' && t('confirmActivateDesc').replace('{name}', confirmAction.name)}
                {confirmAction.type === 'delete' && t('confirmDeleteDesc').replace('{name}', confirmAction.name)}
              </p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setConfirmAction(null)}>{t('cancel')}</Button>
                <Button
                  className={confirmAction.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => {
                    if (confirmAction.type === 'approve') handleApproveCompany(confirmAction.id);
                    if (confirmAction.type === 'suspend') handleSuspendCompany(confirmAction.id);
                    if (confirmAction.type === 'activate') handleActivateCompany(confirmAction.id);
                    if (confirmAction.type === 'delete') handleDeleteCompany(confirmAction.id);
                  }}
                >
                  {t('confirm')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ============ OVERRIDE SUB DIALOG ============ */}
        {overrideCompany && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-bold mb-4">{t('editSubscription')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Plan</label>
                  <select
                    value={overridePlan}
                    onChange={(e) => setOverridePlan(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Statut</label>
                  <select
                    value={overrideStatus}
                    onChange={(e) => setOverrideStatus(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="active">Active</option>
                    <option value="trialing">Trialing</option>
                    <option value="past_due">Past Due</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setOverrideCompany(null)}>{t('cancel')}</Button>
                  <Button onClick={handleOverride}>{t('save')}</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============ RESOLVE INCIDENT DIALOG ============ */}
        {resolveIncident && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-bold mb-4">{t('resolveIncident')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('resolutionNote')} *
                  </label>
                  <textarea
                    value={resolutionNote}
                    onChange={(e) => setResolutionNote(e.target.value)}
                    className="w-full p-2 border rounded-lg"
                    rows={4}
                    maxLength={1000}
                    placeholder={t('describeResolution')}
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setResolveIncident(null)}>{t('cancel')}</Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleResolveIncident}
                    disabled={!resolutionNote.trim()}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" /> {t('resolve')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProfessionalLayout>
  );
};

export default PlatformAdminPage;
